import { Component, computed, HostBinding, input, signal } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { PhoneNumber } from '@fmnts/common/phone';
import {
  AbstractDeclarativeInputFieldComponent,
  FormTextInputField,
} from '@fmnts/declarative-forms';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';
import * as PhoneField from './phone-field.model';

// TODO(declarative-forms): Move to `@fmnts/declarative-forms`
@Component({
  selector: 'next-phone-field',
  templateUrl: './phone-field.component.html',
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule],
})
export class PhoneFieldComponent
  extends AbstractDeclarativeInputFieldComponent<
    FormTextInputField,
    PhoneField.FieldValue
  >
  implements ControlValueAccessor
{
  @HostBinding('class') componentClass =
    'form-field form-field__phone form-field--focus-group';

  /**
   * Value for the `autocomplete` attribute of the input field.
   */
  readonly autocomplete = input(this._getAutocompleteBehavior());

  /** Country code. */
  protected readonly countryCode = signal('');
  /** The phone number without the country code. */
  protected readonly phoneNumber = signal('');
  /** The whole phone number including the country code. */
  protected readonly value = computed(() => {
    const phoneNumber = this.phoneNumber();
    return phoneNumber !== '' ? `${this.countryCode()}${phoneNumber}` : '';
  });

  writeValue(value: unknown) {
    const validValue = Fn.pipe(
      value,
      O.liftPredicate(PhoneField.validateWriteValue),
      O.getOrThrow,
    );

    const newValue = Fn.pipe(
      validValue,
      O.liftNullable((s) => (s === '' ? null : s)),
      // There should always be a default value sent by the backend
      O.getOrElse(Fn.constant(this.field.widgetOptions.defaultValue as string)),
      PhoneNumber.parse,
    );

    this._updateFromUnvalidated(newValue);
  }

  get inputMode(): string {
    const { keyboardType: keyboardType = '' } = this.field.widgetOptions;
    return ['email', 'number', 'tel'].includes(keyboardType)
      ? keyboardType
      : 'numeric';
  }

  get inputType(): string {
    const { keyboardType: keyboardType = '' } = this.field.widgetOptions;
    return ['email', 'number', 'tel'].includes(keyboardType)
      ? keyboardType
      : 'tel';
  }

  get showError(): boolean {
    return (
      this.form &&
      (this.phoneNumber() || this.control.touched) &&
      this.control.invalid
    );
  }

  protected _updateCountryCode(countryCode: string): void {
    this.countryCode.set(countryCode);
    this._onChange(this.value());
    this._onTouched();
  }

  protected _updatePhoneNumber(value: string): void {
    // Check if there was a country code (including + or 00) entered. If so,
    // extract it.
    const unvalidated = PhoneNumber.parse(value);
    if (O.isSome(unvalidated.countryCode)) {
      this._updateFromUnvalidated(unvalidated);
    } else {
      this.phoneNumber.set(value);
    }

    this._onChange(this.value());
    this._onTouched();
  }

  private _updateFromUnvalidated(
    unvalidated: PhoneNumber.UnvalidatedPhoneNumber,
  ): void {
    const { countryCode, phoneNumber } = unvalidated;

    this.countryCode.set(
      countryCode.pipe(
        O.map(PhoneNumber.formatCountryCode),
        O.getOrElse(() => ''),
      ),
    );
    this.phoneNumber.set(phoneNumber);
  }
}
